class ReferenceCaseSearch extends HTMLElement {
  constructor() {
    super();

    this.searchInput = this.querySelector("#filter-search-input");
    this.suggestionsContainer = this.querySelector("#search-suggestions");
    this.articlesContainer = this.querySelector(".blog-articles");
    this.allArticlesData = [];
    this.searchTimeout = null;
    this.currentFocus = -1;

    // Load More functionality
    this.loadMoreBtn = this.querySelector("#load-more-btn");
    this.searchResultsMessage = this.querySelector("#search-results-message");
    this.currentPage = 1;
    this.articlesPerPage = 6;
    this.allArticleElements = [];
    this.isSearchMode = false;
    this.isClickingSuggestion = false;

    // Filter elements
    this.filters = {
      modules: this.querySelector("#filter-modules"),
      industry: this.querySelector("#filter-industry"),
      users: this.querySelector("#filter-users"),
    };
    this.appliedContainer = this.querySelector("#applied-filters");
    
    // Dynamically add tag filter dropdowns
    this.initTagFilters();

    // Bind methods
    this.handleSearchInput = this.handleSearchInput.bind(this);
    this.handleSuggestionClick = this.handleSuggestionClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.hideSuggestions = this.hideSuggestions.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
    this.handleLoadMore = this.handleLoadMore.bind(this);

    this.init();
    this.initFilters();
    this.initLoadMore();
    this.updateArticlesCount();
    this.initShowMoreLess();
  }

  init() {
    if (!this.searchInput) return;

    // Load articles data from server-side JSON
    this.loadArticlesData();

    // Get all article elements
    this.allArticleElements = Array.from(
      this.querySelectorAll(".blog-articles__article")
    );
    console.log(
      "Total article elements found in DOM:",
      this.allArticleElements.length
    );

    // Debug: List all articles found
    this.allArticleElements.forEach((el, index) => {
      const title =
        el.querySelector("h3, h2, .article-title")?.textContent?.trim() ||
        "No title found";
      const id = el.getAttribute("data-article-id");
      console.log(`Article ${index + 1}: "${title}" (ID: ${id})`);
    });

    // Add event listeners
    this.searchInput.addEventListener("input", this.handleSearchInput);
    this.searchInput.addEventListener("keydown", this.handleKeydown);
    this.searchInput.addEventListener(
      "focus",
      this.handleSearchFocus.bind(this)
    );
    this.searchInput.addEventListener("blur", this.handleSearchBlur.bind(this));

    // Add click event listener to suggestions container (event delegation)
    if (this.suggestionsContainer) {
      this.suggestionsContainer.addEventListener("click", (event) =>
        this.handleSuggestionClick(event)
      );

      // Track when we're clicking on suggestions
      this.suggestionsContainer.addEventListener("mousedown", (event) => {
        this.isClickingSuggestion = true;
      });

      this.suggestionsContainer.addEventListener("mouseup", (event) => {
        setTimeout(() => {
          this.isClickingSuggestion = false;
        }, 50);
      });
    }

    // Hide suggestions when clicking outside
    document.addEventListener("click", (e) => {
      // Check if click is outside the search component
      const searchComponent = e.target.closest(".filter-search");
      const suggestionsComponent = e.target.closest(".search-suggestions");

      if (!searchComponent && !suggestionsComponent) {
        this.hideSuggestions();
      }
    });
  }

  initLoadMore() {
    if (!this.loadMoreBtn) return;

    // Get initial values from button
    this.articlesPerPage = parseInt(
      this.loadMoreBtn.dataset.articlesPerPage || "6"
    );
    this.currentPage = parseInt(this.loadMoreBtn.dataset.currentPage || "1");

    // Add click listener
    this.loadMoreBtn.addEventListener("click", this.handleLoadMore);

    // Update load more button visibility
    this.updateLoadMoreButton();
  }

  handleLoadMore() {
    if (this.isSearchMode) return; // Don't load more in search mode

    this.currentPage++;
    this.showArticlesByPage();
    this.updateLoadMoreButton();
    
    // Reinitialize show more/less for newly shown articles
    setTimeout(() => {
      this.initShowMoreLess();
    }, 100);
  }

  showArticlesByPage() {
    const startIndex = 0;
    const endIndex = this.currentPage * this.articlesPerPage;

    this.allArticleElements.forEach((article, index) => {
      if (index < endIndex) {
        article.style.display = "block";
        article.removeAttribute("data-search-hidden");
      } else {
        // Hide articles that are beyond the current page
        article.style.display = "none";
      }
    });
    this.updateArticlesCount();
  }

  updateLoadMoreButton() {
    if (!this.loadMoreBtn) return;

    const totalArticles = this.allArticleElements.length;
    const shownArticles = this.currentPage * this.articlesPerPage;

    if (shownArticles >= totalArticles || this.isSearchMode) {
      this.loadMoreBtn.style.display = "none";
    } else {
      this.loadMoreBtn.style.display = "block";
    }

    // Update button data
    this.loadMoreBtn.dataset.currentPage = this.currentPage.toString();

    // Update articles counter
    this.updateArticlesCounter();
  }

  updateArticlesCounter() {
    const shownCountElement = document.getElementById("articles-shown-count");
    const totalCountElement = document.getElementById("articles-total-count");

    if (shownCountElement && totalCountElement) {
      let shown;
      if (this.isSearchMode) {
        shown = this.allArticleElements.filter(
          (el) =>
            el.style.display !== "none" &&
            el.hasAttribute("data-search-visible")
        ).length;
      } else {
        shown = this.allArticleElements.filter(
          (el) => el.style.display !== "none"
        ).length;
      }

      shownCountElement.textContent = shown;
      totalCountElement.textContent = this.allArticleElements.length;
    }
  }

  enterSearchMode(query, matchingArticles) {
    console.log(
      "Entering search mode with:",
      matchingArticles.length,
      "matching articles"
    );
    this.isSearchMode = true;
    this.setAttribute("data-search-mode", "true");

    // Get IDs of matching articles
    const matchingIds = matchingArticles.map((article) =>
      article.id.toString()
    );
    console.log("Matching article IDs:", matchingIds);

    // Force hide ALL articles first with multiple methods
    this.allArticleElements.forEach((article, index) => {
      const articleId = article.getAttribute("data-article-id");

      if (matchingIds.includes(articleId)) {
        // Show matching articles
        article.style.setProperty("display", "block", "important");
        article.style.setProperty("visibility", "visible", "important");
        article.classList.remove("search-hidden");
        article.classList.add("search-visible");
        article.removeAttribute("data-search-hidden");
        article.setAttribute("data-search-visible", "true");
        console.log(`Showing article ${index}: ${articleId}`);
      } else {
        // Hide non-matching articles
        article.style.setProperty("display", "none", "important");
        article.style.setProperty("visibility", "hidden", "important");
        article.classList.remove("search-visible");
        article.classList.add("search-hidden");
        article.setAttribute("data-search-hidden", "true");
        article.removeAttribute("data-search-visible");
        console.log(`Hiding article ${index}: ${articleId}`);
      }
    });

    console.log("Total matching articles processed:", matchingIds.length);

    // Force re-check after a tiny delay to ensure CSS has been applied
    setTimeout(() => {
      // Debug: Check computed styles
      const allPageArticles = document.querySelectorAll(
        ".blog-articles__article"
      );
      let visibleCount = 0;

      allPageArticles.forEach((el, index) => {
        const computedStyle = window.getComputedStyle(el);
        const isVisible =
          computedStyle.display !== "none" &&
          computedStyle.visibility !== "hidden";
        const title =
          el.querySelector("h3, h2, .article-title")?.textContent?.trim() ||
          "No title";
        const id = el.getAttribute("data-article-id");

        if (isVisible) {
          visibleCount++;
          console.log(
            `STILL VISIBLE: Article ${index + 1}: "${title}" (ID: ${id})`
          );
          console.log("Computed display:", computedStyle.display);
          console.log("Computed visibility:", computedStyle.visibility);
          console.log("Inline style:", el.style.cssText);
          console.log("Classes:", el.className);
        }
      });

      console.log("FINAL CHECK: Total visible articles:", visibleCount);
      console.log("Expected visible articles:", matchingIds.length);

      if (visibleCount !== matchingIds.length) {
        console.error(
          "❌ MISMATCH: Expected",
          matchingIds.length,
          "visible articles but found",
          visibleCount
        );
      } else {
        console.log("✅ SUCCESS: Article visibility matches expected count");
      }
    }, 100);

    // Update search results message
    this.updateSearchResultsMessage(query, matchingArticles.length);

    // Hide load more button
    this.updateLoadMoreButton();
    this.updateArticlesCount();
  }

  exitSearchMode() {
    console.log("Exiting search mode");
    this.isSearchMode = false;
    this.removeAttribute("data-search-mode");

    // Remove search attributes, classes AND inline styles from all articles
    this.allArticleElements.forEach((article, index) => {
      // Remove classes and attributes
      article.classList.remove("search-hidden", "search-visible");
      article.removeAttribute("data-search-hidden");
      article.removeAttribute("data-search-visible");

      // Remove the inline styles that were set with setProperty
      article.style.removeProperty("display");
      article.style.removeProperty("visibility");

      console.log(`Reset article ${index}: removed inline styles`);
    });

    // Reset to show only the articles that should be visible based on current pagination
    this.showArticlesByPage();

    // Hide search results message
    if (this.searchResultsMessage) {
      this.searchResultsMessage.style.display = "none";
    }

    // Show load more button if needed
    this.updateLoadMoreButton();
    this.updateArticlesCount();

    console.log("Search mode exit complete");
  }

  updateSearchResultsMessage(query, count) {
    if (!this.searchResultsMessage) return;

    const messageElement = this.searchResultsMessage.querySelector("p");
    if (messageElement) {
      if (count === 0) {
        messageElement.textContent = `No results found for "${query}"`;
      } else {
        messageElement.textContent = `Found ${count} result${
          count !== 1 ? "s" : ""
        } for "${query}"`;
      }
    }
    this.searchResultsMessage.style.display = "block";
  }

  loadArticlesData() {
    try {
      const articlesScript = document.getElementById("articles-data");
      if (articlesScript) {
        this.allArticlesData = JSON.parse(articlesScript.textContent);
      } else {
      }
    } catch (error) {
      this.allArticlesData = [];
    }
  }

  handleSearchInput(event) {
    const query = event.target.value.trim();

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // If query is empty, exit search mode
    if (query.length === 0) {
      this.exitSearchMode();
      this.hideSuggestions();
      return;
    }

    // Debounce the search
    this.searchTimeout = setTimeout(() => {
      if (query.length >= 2) {
        this.performSearch(query);
        // Also filter articles in real-time
        this.performArticleSearch(query);
      } else {
        this.hideSuggestions();
        this.exitSearchMode();
      }
    }, 300);
  }

  handleSearchFocus(event) {
    const query = event.target.value.trim();

    // Only show suggestions if there's a valid query
    if (query.length >= 2) {
      this.performSearch(query);
    }
  }

  handleSearchBlur(event) {
    // Add a delay to allow for click events on suggestions
    setTimeout(() => {
      // Only hide if we're not currently clicking on a suggestion
      if (!this.isClickingSuggestion) {
        this.hideSuggestions();
      }
    }, 200);
  }

  performSearch(query) {
    const lowerQuery = query.toLowerCase();
    const suggestions = this.generateSuggestions(lowerQuery);

    // Show suggestions
    this.displaySuggestions(suggestions);
  }

  generateSuggestions(query) {
    const lowerQuery = query.toLowerCase();
    const suggestions = {
      terms: new Set(),
      tags: new Set(),
      articles: [],
      popular: new Set(),
    };

    // Common business/industry terms that might be helpful
    const popularTerms = [
      "crm",
      "erp",
      "ecommerce",
      "inventory",
      "accounting",
      "sales",
      "manufacturing",
      "retail",
      "wholesale",
      "reporting",
      "analytics",
      "automation",
      "integration",
      "workflow",
      "dashboard",
      "management",
    ];

    // Add popular terms that match
    popularTerms.forEach((term) => {
      if (term.includes(lowerQuery) && lowerQuery.length >= 2) {
        suggestions.popular.add(term);
      }
    });

    this.allArticlesData.forEach((article) => {
      // Search in title for terms
      if (article.title.toLowerCase().includes(lowerQuery)) {
        const titleWords = this.cleanAndSplitText(article.title.toLowerCase());
        titleWords.forEach((word) => {
          if (
            word.includes(lowerQuery) &&
            word.length > 2 &&
            word.length < 20
          ) {
            suggestions.terms.add(word);
          }
        });
      }

      // Search in content for terms
      if (article.content) {
        const contentWords = this.cleanAndSplitText(
          article.content.toLowerCase()
        );
        const matchingWords = contentWords.filter(
          (word) =>
            word.includes(lowerQuery) && word.length > 2 && word.length < 15
        );
        // Add more relevant terms from content
        matchingWords.slice(0, 3).forEach((word) => {
          suggestions.terms.add(word);
        });
      }

      // Search in tags
      article.tags.forEach((tag) => {
        if (tag.toLowerCase().includes(lowerQuery)) {
          suggestions.tags.add(tag.toLowerCase());
        }
      });
    });

    // Get article suggestions separately
    suggestions.articles = this.getArticleSuggestions(query).slice(0, 3);

    return this.formatSuggestions(suggestions, query);
  }

  cleanAndSplitText(text) {
    // Remove punctuation and split into words
    return text
      .replace(/[^\w\s]/g, " ")
      .split(/\s+/)
      .filter((word) => word.length > 0);
  }

  formatSuggestions(suggestions, query) {
    const formatted = [];
    const maxSuggestions = 8;
    let count = 0;

    // Add popular terms first (if any)
    if (suggestions.popular.size > 0 && count < maxSuggestions) {
      const popularArray = Array.from(suggestions.popular).slice(0, 2);
      popularArray.forEach((term) => {
        if (count < maxSuggestions) {
          formatted.push({
            type: "popular",
            value: term,
            display: `${term} (popular)`,
            category: "Popular searches",
          });
          count++;
        }
      });
    }

    // Add tag suggestions
    if (suggestions.tags.size > 0 && count < maxSuggestions) {
      const tagsArray = Array.from(suggestions.tags).slice(0, 2);
      tagsArray.forEach((tag) => {
        if (count < maxSuggestions) {
          formatted.push({
            type: "tag",
            value: tag,
            display: `#${tag}`,
            category: "Tags",
          });
          count++;
        }
      });
    }

    // Add term suggestions
    if (suggestions.terms.size > 0 && count < maxSuggestions) {
      const termsArray = Array.from(suggestions.terms)
        .filter((term) => !suggestions.popular.has(term)) // Avoid duplicates
        .slice(0, 3);

      termsArray.forEach((term) => {
        if (count < maxSuggestions) {
          formatted.push({
            type: "term",
            value: term,
            display: term,
            category: "Suggested terms",
          });
          count++;
        }
      });
    }

    return formatted;
  }

  displaySuggestions(suggestions) {
    const query = this.searchInput?.value?.trim() || "";

    // If no query, hide suggestions
    if (query.length < 2) {
      this.hideSuggestions();
      return;
    }

    const termSuggestions = suggestions || this.generateSuggestions(query);
    const articleSuggestions = this.getArticleSuggestions(query).slice(0, 5);

    // If no suggestions at all, hide
    if (termSuggestions.length === 0 && articleSuggestions.length === 0) {
      this.hideSuggestions();
      return;
    }

    // Create two-column layout
    let suggestionHTML = '<div class="suggestions-container">';

    // Left column - Terms
    suggestionHTML += '<div class="suggestions-left">';
    if (termSuggestions.length > 0) {
      suggestionHTML += '<div class="suggestions-header">Search Terms</div>';
      termSuggestions.forEach((suggestion, index) => {
        suggestionHTML += `<div data-index="${index}" data-type="${
          suggestion.type
        }" data-value="${suggestion.value}" class="suggestion-item suggestion-${
          suggestion.type
        }">
          <span class="suggestion-icon">${this.getSuggestionIcon(
            suggestion.type
          )}</span>
          <span class="suggestion-text">${suggestion.display}</span>
        </div>`;
      });
    }
    suggestionHTML += "</div>";

    // Right column - Articles
    suggestionHTML += '<div class="suggestions-right">';
    if (articleSuggestions.length > 0) {
      suggestionHTML += '<div class="suggestions-header">references</div>';
      articleSuggestions.forEach((article, index) => {
        const adjustedIndex = termSuggestions.length + index;
        suggestionHTML += `<div data-index="${adjustedIndex}" data-type="article" data-url="${
          article.metafield_url || article.url
        }" class="suggestion-item suggestion-article">
          <span class="suggestion-icon">📄</span>
          <span class="suggestion-text">${article.title}</span>
        </div>`;
      });
    }
    suggestionHTML += "</div>";

    suggestionHTML += "</div>";

    this.suggestionsContainer.innerHTML = suggestionHTML;
    this.showSuggestions();
    this.currentFocus = -1;
  }

  getSuggestionIcon(type) {
    const icons = {
      popular: "🔥",
      tag: "🏷️",
      term: "🔍",
      article: "📄",
    };
    return icons[type] || "💡";
  }

  getArticleSuggestions(query) {
    const lowerQuery = query.toLowerCase();
    if (lowerQuery.length < 2) return [];

    return this.allArticlesData
      .filter((article) => {
        return (
          article.title.toLowerCase().includes(lowerQuery) ||
          (article.content &&
            article.content.toLowerCase().includes(lowerQuery)) ||
          (article.excerpt &&
            article.excerpt.toLowerCase().includes(lowerQuery)) ||
          article.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
        );
      })
      .slice(0, 3); // Limit to 3 article suggestions
  }

  showSuggestions() {
    if (this.suggestionsContainer) {
      this.suggestionsContainer.style.display = "block";
    }
  }

  hideSuggestions() {
    if (this.suggestionsContainer) {
      this.suggestionsContainer.style.display = "none";
      this.suggestionsContainer.innerHTML = ""; // Clear suggestions content
      this.currentFocus = -1;
    }
  }

  handleKeydown(event) {
    const suggestions =
      this.suggestionsContainer.querySelectorAll(".suggestion-item");

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.currentFocus++;
      if (this.currentFocus >= suggestions.length) this.currentFocus = 0;
      this.setActiveSuggestion(suggestions);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.currentFocus--;
      if (this.currentFocus < 0) this.currentFocus = suggestions.length - 1;
      this.setActiveSuggestion(suggestions);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (this.currentFocus > -1 && suggestions[this.currentFocus]) {
        this.selectSuggestion(suggestions[this.currentFocus]);
      } else {
        // If no valid suggestion selected, perform search with current input
        const query = this.searchInput.value.trim();
        if (query) {
          this.hideSuggestions();
          this.performArticleSearch(query);
        }
      }
    } else if (event.key === "Escape") {
      this.hideSuggestions();
      this.searchInput.blur();
    }
  }

  setActiveSuggestion(suggestions) {
    suggestions.forEach((suggestion, index) => {
      suggestion.classList.toggle("active", index === this.currentFocus);
    });
  }

  selectSuggestion(suggestionElement) {
    const type = suggestionElement.getAttribute("data-type");
    const url = suggestionElement.getAttribute("data-url");
    const value = suggestionElement.getAttribute("data-value");

    if (type === "article" && url) {
      // Direct navigation to metafield URL or article URL
      window.location.href = url;
    } else if (
      value &&
      (type === "term" || type === "tag" || type === "popular")
    ) {
      // For term suggestions, fill the search input and perform search
      this.searchInput.value = value;
      this.hideSuggestions();
      this.performArticleSearch(value);
    }
  }

  handleSuggestionClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const suggestionItem = event.target.closest(".suggestion-item");

    if (suggestionItem) {
      this.selectSuggestion(suggestionItem);
    }
  }

  // Removed filterCurrentArticles - not needed due to pagination

  // Removed unnecessary search methods - only using suggestions now

  // Initialize search from URL parameter on page load
  initializeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get("search");

    if (searchQuery) {
      this.searchInput.value = searchQuery;
      // Just show suggestions, don't filter articles
      this.performSearch(searchQuery);
    }
  }

  // Filter-related methods
  initFilters() {
    // Add event listeners for dropdowns
    Object.entries(this.filters).forEach(([key, select]) => {
      if (select) {
        select.addEventListener("change", this.onSelectChange);
      } else {
      }
    });

    // Initialize filters from URL
    this.applyFiltersFromURL();
  }

  initTagFilters() {
    // Find all tag filter dropdowns dynamically
    const tagFilterSelects = this.querySelectorAll(".tag-filter-select");
    tagFilterSelects.forEach((select) => {
      const filterId = select.id;
      if (filterId && !this.filters[filterId]) {
        // Use the ID as the key (without #)
        const key = filterId.replace("#", "");
        this.filters[key] = select;
        select.addEventListener("change", this.onSelectChange);
      }
    });
  }

  getSelectedTags() {
    const tags = Object.entries(this.filters)
      .map(([key, select]) => {
        const value = select?.value;
        return value;
      })
      .filter(Boolean);

    return tags;
  }

  getBlogHandle() {
    // Extract blog handle from current URL or data attribute
    const pathParts = window.location.pathname.split("/");
    const blogIndex = pathParts.indexOf("blogs");
    const handle =
      blogIndex !== -1 && pathParts[blogIndex + 1]
        ? pathParts[blogIndex + 1]
        : null;
    return handle;
  }

  recheckFilterElements() {
    // Re-query filter elements in case they weren't ready initially
    const newFilters = {
      modules: this.querySelector("#filter-modules"),
      industry: this.querySelector("#filter-industry"),
      users: this.querySelector("#filter-users"),
    };

    // Update any missing elements
    Object.entries(newFilters).forEach(([key, element]) => {
      if (element && !this.filters[key]) {
        this.filters[key] = element;
        element.addEventListener("change", this.onSelectChange);
      }
    });

    // Re-check tag filter dropdowns
    this.initTagFilters();

    // Also recheck search input if missing
    if (!this.searchInput) {
      this.searchInput = this.querySelector("#filter-search-input");
      if (this.searchInput) {
        this.searchInput.addEventListener("input", this.handleSearchInput);
        this.searchInput.addEventListener("keydown", this.handleKeydown);
        this.searchInput.addEventListener(
          "focus",
          this.handleSearchFocus.bind(this)
        );
        this.searchInput.addEventListener(
          "blur",
          this.handleSearchBlur.bind(this)
        );
      }
    }
  }

  updateURL(tags = [], searchQuery = "") {
    const uniqueTags = [...new Set(tags)];
    const blogHandle = this.getBlogHandle() || "odoo-reference";

    let url;
    if (uniqueTags.length > 0) {
      url = `/blogs/${blogHandle}/tagged/${uniqueTags.join(
        ","
      )}?view=${blogHandle}`;
    } else {
      url = `/blogs/${blogHandle}?view=${blogHandle}`;
    }

    if (searchQuery.trim()) {
      url +=
        (url.includes("?") ? "&" : "?") +
        `search=${encodeURIComponent(searchQuery)}`;
    }

    window.location.href = url;
  }

  applyFiltersFromURL() {
    const url = new URL(window.location.href);
    const pathParts = url.pathname.split("/tagged/");
    if (pathParts.length > 1) {
      const tags = pathParts[1].split("?")[0].split(",");
      tags.forEach((tag) => {
        this.createTagBadge(tag);
        // Preselect dropdown values in all filters (including dynamic tag filters)
        Object.entries(this.filters).forEach(([key, select]) => {
          if (select) {
            Array.from(select.options).forEach((opt) => {
              if (opt.value === tag) {
                opt.selected = true;
              }
            });
          }
        });
      });
    }
  }

  createResetButton() {
    let resetBtn = document.getElementById("reset-button");
    if (!resetBtn && this.appliedContainer) {
      resetBtn = document.createElement("div");
      resetBtn.className = "reset-btn";
      resetBtn.id = "reset-button";
      resetBtn.innerHTML =
        'Reset Filters <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="14" y="6.88892" width="10.0566" height="1.25708" rx="0.5" transform="rotate(135 14 6.88892)" fill="#001433"/><rect x="6.88867" y="6" width="10.0566" height="1.25708" rx="0.5" transform="rotate(45 6.88867 6)" fill="#001433"/></svg>';
      resetBtn.onclick = () => {
        this.updateURL([], "");
        this.searchInput.value = "";
        this.hideSuggestions();
      };
      this.appliedContainer.appendChild(resetBtn);
    } else if (resetBtn && this.appliedContainer) {
      // Move it to the end if already exists
      this.appliedContainer.appendChild(resetBtn);
    }
  }

  createTagBadge(tag) {
    if (!this.appliedContainer?.querySelector(`[data-tag="${tag}"]`)) {
      const span = document.createElement("div");
      span.className = "filter-tag";
      span.setAttribute("data-tag", tag);

      // Get filter labels from the JSON script tag
      let displayLabel = tag
        .replace(/-/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());

      try {
        // First try the original filter-labels-data
        const filterLabelsScript =
          document.getElementById("filter-labels-data");
        if (filterLabelsScript) {
          const filterLabels = JSON.parse(filterLabelsScript.textContent);
          if (filterLabels[tag]) {
            displayLabel = `${filterLabels[tag].category} : ${filterLabels[tag].label}`;
          }
        }
        
        // Also check tag-filter-labels-data for dynamic tag filters
        if (displayLabel === tag.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())) {
          const tagFilterLabelsScript =
            document.getElementById("tag-filter-labels-data");
          if (tagFilterLabelsScript) {
            const tagFilterLabels = JSON.parse(tagFilterLabelsScript.textContent);
            if (tagFilterLabels[tag]) {
              displayLabel = `${tagFilterLabels[tag].category} : ${tagFilterLabels[tag].label}`;
            }
          }
        }
      } catch (error) {}

      span.innerHTML = `${displayLabel} <button onclick="window.removeFilterTag('${tag}')">&times;</button>`;
      const resetBtn = document.getElementById("reset-button");
      if (resetBtn) {
        this.appliedContainer.insertBefore(span, resetBtn);
      } else {
        this.appliedContainer.appendChild(span);
      }
    }

    this.createResetButton();
  }

  onSelectChange(event) {
    const selected = this.getSelectedTags();
    const currentSearch = this.searchInput?.value || "";

    this.updateURL(selected, currentSearch);
  }

  connectedCallback() {
    // Give a small delay to ensure DOM is fully ready
    setTimeout(() => {
      // Re-check filter elements in case they weren't ready during constructor
      this.recheckFilterElements();
      this.initializeFromURL();
    }, 100);

    // Add click handler for suggestions
    this.suggestionsContainer?.addEventListener(
      "click",
      this.handleSuggestionClick
    );

    // Make removeFilterTag globally accessible
    window.removeFilterTag = (tagToRemove) => {
      const url = new URL(window.location.href);
      const tags = url.pathname.includes("/tagged/")
        ? url.pathname.split("/tagged/")[1].split("?")[0].split(",")
        : [];
      const updatedTags = tags.filter((tag) => tag !== tagToRemove);
      const currentSearch = this.searchInput?.value || "";
      this.updateURL(updatedTags, currentSearch);
    };
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.suggestionsContainer?.removeEventListener(
      "click",
      this.handleSuggestionClick
    );

    // Clean up global function
    delete window.removeFilterTag;
  }

  performArticleSearch(query) {
    console.log("Performing article search for:", query);
    const matchingArticles = this.getMatchingArticles(query);
    console.log("Found matching articles:", matchingArticles.length);
    this.enterSearchMode(query, matchingArticles);
  }

  getMatchingArticles(query) {
    const lowerQuery = query.toLowerCase();

    const matchingArticles = this.allArticlesData.filter((article) => {
      const matches =
        article.title.toLowerCase().includes(lowerQuery) ||
        (article.content &&
          article.content.toLowerCase().includes(lowerQuery)) ||
        (article.excerpt &&
          article.excerpt.toLowerCase().includes(lowerQuery)) ||
        (article.summary &&
          article.summary.toLowerCase().includes(lowerQuery)) ||
        article.tags.some((tag) => tag.toLowerCase().includes(lowerQuery));

      if (matches) {
        console.log(
          "Matching article found:",
          article.title,
          "ID:",
          article.id
        );
      }

      return matches;
    });

    return matchingArticles;
  }

  updateArticlesCount() {
    const total = this.allArticleElements.length;
    let shown = 0;
    if (this.isSearchMode) {
      shown = this.allArticleElements.filter(
        (el) =>
          el.style.display !== "none" && el.hasAttribute("data-search-visible")
      ).length;
    } else {
      shown = this.allArticleElements.filter(
        (el) => el.style.display !== "none"
      ).length;
    }

    // Get the count display element
    const countDisplayEl = this.querySelector(".blog-articles-count");

    // Hide count display when in search mode
    if (countDisplayEl) {
      if (this.isSearchMode) {
        countDisplayEl.style.display = "none";
      } else {
        countDisplayEl.style.display = "block";
      }
    }

    const shownCountEl = document.getElementById("articles-shown-count");
    const totalCountEl = document.getElementById("articles-total-count");
    if (shownCountEl) shownCountEl.textContent = shown;
    if (totalCountEl) totalCountEl.textContent = total;
  }

  initShowMoreLess() {
    this.querySelectorAll(".article-card-wrap").forEach((card) => {
      const tagsTitle = card.querySelector(".tags-title");
      const showMoreLessContainer = card.querySelector(".show-more-less");

      if (!tagsTitle || !showMoreLessContainer) return;

      // Check if content height exceeds 162px
      const checkHeight = () => {
        tagsTitle.classList.remove("collapsed", "expanded");
        const fullHeight = tagsTitle.scrollHeight;
        
        if (fullHeight > 162) {
          tagsTitle.classList.add("collapsed");
          showMoreLessContainer.classList.add("active");
        } else {
          showMoreLessContainer.classList.remove("active");
        }
      };

      checkHeight();

      // Handle show more/less clicks
      showMoreLessContainer.addEventListener("click", (e) => {
        if (e.target.classList.contains("case-show-more")) {
          e.preventDefault();
          tagsTitle.classList.remove("collapsed");
          tagsTitle.classList.add("expanded");
        } else if (e.target.classList.contains("case-show-less")) {
          e.preventDefault();
          tagsTitle.classList.remove("expanded");
          tagsTitle.classList.add("collapsed");
          card.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });

      window.addEventListener("resize", checkHeight);
    });
  }
}

// Define the custom element
customElements.define("reference-case-search", ReferenceCaseSearch);
